import {IUserRoleService} from "@/services/IUserRoleService";
import {inject, injectable} from "tsyringe";
import {IAuthService, User} from "frontend-core";
import {UserRoleEnum} from "@/models/UserRoleEnum";

@injectable()
export class UserRoleService implements IUserRoleService {
    constructor(
        @inject("IAuthService")
        private readonly authService: IAuthService,
    ) {
    }

    get isSuperAdmin(): boolean {
        return !!this.authService.user?.roles.includes(UserRoleEnum.superAdmin);
    }

    get isAdmin(): boolean {
        return this.authService.user ? this.isUserAdmin(this.authService.user) : false;
    }

    get isMentor(): boolean {
        return this.authService.user ? this.isUserMentor(this.authService.user) : false;
    }

    get isCurator(): boolean {
        return this.authService.user ? this.isUserCurator(this.authService.user) : false;
    }

    get isSubCurator(): boolean {
        return this.authService.user ? this.isUserSubCurator(this.authService.user) : false;
    }

    get isAgent(): boolean {
        return this.authService.user ? this.isUserAgent(this.authService.user) : false;
    }

    get isSubAgent(): boolean {
        return this.authService.user ? this.isUserSubAgent(this.authService.user) : false;
    }

    get isTechSupport(): boolean {
        return this.authService.user ? this.isUserTechSupport(this.authService.user) : false;
    }

    get isAllowSegmentationRead(): boolean {
        return !!this.authService.user?.roles.find((i) => new Array<string>(
            UserRoleEnum.allowSegmentationRead,
            UserRoleEnum.techSupport,
            UserRoleEnum.superAdmin,
            UserRoleEnum.admin,
            UserRoleEnum.mentor,
            UserRoleEnum.curator,
            UserRoleEnum.subCurator,
            UserRoleEnum.agent,
            UserRoleEnum.subAgent,
            UserRoleEnum.allowSegmentationEdit,
            UserRoleEnum.allowOtherSegmentationEdit,
            UserRoleEnum.allowOtherSegmentationRead,
        ).includes(i));
    }

    get isAllowSegmentationEdit(): boolean {
        return !!this.authService.user?.roles.find((i) => new Array<string>(
            UserRoleEnum.allowSegmentationEdit,
            UserRoleEnum.superAdmin,
            UserRoleEnum.admin,
            UserRoleEnum.allowOtherSegmentationEdit,
        ).includes(i));
    }

    get isAllowOtherSegmentationRead(): boolean {
        return !!this.authService.user?.roles.find((i) => new Array<string>(
            UserRoleEnum.superAdmin,
            UserRoleEnum.admin,
            UserRoleEnum.allowOtherSegmentationEdit,
            UserRoleEnum.allowOtherSegmentationRead,
            UserRoleEnum.techSupport,
        ).includes(i));
    }

    get isAllowOtherSegmentationEdit(): boolean {
        return !!this.authService.user?.roles.find((i) => new Array<string>(
            UserRoleEnum.superAdmin,
            UserRoleEnum.admin,
            UserRoleEnum.allowOtherSegmentationEdit,
        ).includes(i));
    }

    get isAllowBlockAgent(): boolean {
        return !!this.authService.user?.roles.find((i) => new Array<string>(
            UserRoleEnum.superAdmin,
            UserRoleEnum.admin,
            UserRoleEnum.curator,
            UserRoleEnum.mentor,
            UserRoleEnum.allowBlockAgent,
        ).includes(i));
    }

    get isAllowBlockOtherAgent(): boolean {
        return !!this.authService.user?.roles.find((i) => new Array<string>(
            UserRoleEnum.superAdmin,
            UserRoleEnum.admin,
            UserRoleEnum.allowBlockOtherAgent,
        ).includes(i));
    }

    get isAllowCuratorEmailEdit(): boolean {
        return !!this.authService.user?.roles.find((i) => new Array<string>(
            UserRoleEnum.superAdmin,
            UserRoleEnum.admin,
            UserRoleEnum.curator,
            UserRoleEnum.mentor,
            UserRoleEnum.allowCuratorEmailEdit,
        ).includes(i));
    }

    get isAllowOtherCuratorEmailEdit(): boolean {
        return !!this.authService.user?.roles.find((i) => new Array<string>(
            UserRoleEnum.superAdmin,
            UserRoleEnum.admin,
            UserRoleEnum.allowOtherCuratorEmailEdit,
        ).includes(i));
    }

    get isAllowToEditAcquiring(): boolean {
        return !!this.authService.user?.roles.find((i) => new Array<string>(
            UserRoleEnum.superAdmin,
            UserRoleEnum.admin,
        ).includes(i));
    }

    get isHasChangeParentRole(): boolean {
        return !!this.authService.user?.roles.includes(UserRoleEnum.parentChanger);
    }

    isUserSuperAdmin(user: User): boolean {
        return user.roles.includes(UserRoleEnum.superAdmin);
    }

    isUserAdmin(user: User): boolean {
        return user.roles.includes(UserRoleEnum.admin);
    }

    isUserAgent(user: User): boolean {
        return user.roles.includes(UserRoleEnum.agent);
    }

    isUserCurator(user: User): boolean {
        return user.roles.includes(UserRoleEnum.curator);
    }

    isUserSubCurator(user: User): boolean {
        return user.roles.includes(UserRoleEnum.subCurator);
    }

    isUserMentor(user: User): boolean {
        return user.roles.includes(UserRoleEnum.mentor);
    }

    isUserSubAgent(user: User): boolean {
        return user.roles.includes(UserRoleEnum.subAgent);
    }

    isUserTechSupport(user: User): boolean {
        return user.roles.includes(UserRoleEnum.techSupport);
    }

    isUserCanChangeParent(user: User | undefined): boolean {
        return user !== undefined ? user.roles.includes(UserRoleEnum.parentChanger) : false;
    }
}
